import { Search } from 'lucide-react';
import {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';

import { cx, fieldControlClass } from '@/components/ui/shared';
import {
  canSearchConsoleDevices,
  getConsoleSearchPlaceholder,
  searchConsoleRoutes,
  type ConsoleRouteContext,
} from '@/navigation/consoleNavigation';

import { searchFleetDevices } from './consoleDeviceSearch';
import {
  DeviceSearchResults,
  PageSearchResults,
  SEARCH_RESULT_SELECTOR,
} from './ConsoleSearchResults';
import { useConsoleFleet } from './useConsoleFleet';

interface ConsoleTaskSearchProps {
  context: ConsoleRouteContext;
  onNavigate: () => void;
}

const counted = (count: number, singular: string, plural: string) =>
  `${count} ${count === 1 ? singular : plural}`;

/** Finds devices (by name or ID) and pages this person can open. */
const ConsoleTaskSearch = forwardRef<HTMLInputElement, ConsoleTaskSearchProps>(
  ({ context, onNavigate }, ref) => {
    const [query, setQuery] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);
    const resultsRef = useRef<HTMLDivElement>(null);
    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    const trimmedQuery = query.trim();
    const canSearchDevices = canSearchConsoleDevices(context);
    const fleetQuery = useConsoleFleet(canSearchDevices);
    const routes = useMemo(() => searchConsoleRoutes(context, query), [context, query]);
    const devices = useMemo(
      () => searchFleetDevices(fleetQuery.data?.devices ?? [], query),
      [fleetQuery.data?.devices, query],
    );

    const searchingDevices = canSearchDevices && trimmedQuery.length > 0;
    const devicesLoading = searchingDevices && fleetQuery.isLoading;
    const devicesFailed = searchingDevices && fleetQuery.isError;
    const devicesKnown = searchingDevices && !devicesLoading && !devicesFailed;
    const showDevices = devicesLoading || devicesFailed || (devicesKnown && devices.total > 0);

    const pageCount = counted(routes.length, 'page', 'pages');
    const statusText = !trimmedQuery
      ? `${pageCount} available.`
      : devicesKnown
        ? `${pageCount} and ${counted(devices.total, 'device', 'devices')} match “${trimmedQuery}”.`
        : `${pageCount} match “${trimmedQuery}”.`;

    const resultLinks = () =>
      Array.from(
        resultsRef.current?.querySelectorAll<HTMLAnchorElement>(SEARCH_RESULT_SELECTOR) ?? [],
      );

    /** Up and down arrows move from the search box through the results and back. */
    const moveBetweenResults = (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      const links = resultLinks();
      if (links.length === 0) return;
      event.preventDefault();
      const index = links.findIndex((link) => link === document.activeElement);
      if (event.key === 'ArrowDown') {
        links[Math.min(index + 1, links.length - 1)].focus();
      } else if (index <= 0) {
        inputRef.current?.focus();
      } else {
        links[index - 1].focus();
      }
    };

    /** Enter in the search box opens the first result. */
    const openFirstResult = (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== 'Enter' || event.nativeEvent.isComposing) return;
      const [first] = resultLinks();
      if (!first) return;
      event.preventDefault();
      first.click();
    };

    return (
      <div className="grid gap-4" onKeyDown={moveBetweenResults}>
        <div>
          <label htmlFor="console-task-search" className="text-control font-semibold text-ink-strong">
            {canSearchDevices ? 'Search devices and pages' : 'Search pages'}
          </label>
          <p className="mt-1 text-meta text-ink-muted">
            {canSearchDevices ? 'Type a device name or ID, or a page name.' : 'Type a page name.'}{' '}
            Press Enter to open the first result, or use the arrow keys to move through them.
          </p>
          <div className="relative mt-3">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted"
              aria-hidden="true"
            />
            <input
              ref={inputRef}
              id="console-task-search"
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={openFirstResult}
              placeholder={getConsoleSearchPlaceholder(context)}
              className={cx(fieldControlClass, 'pl-10')}
            />
          </div>
        </div>

        <p className="text-meta text-ink-muted" role="status" aria-live="polite">
          {statusText}
        </p>

        <div ref={resultsRef} className="grid gap-5">
          {showDevices ? (
            <DeviceSearchResults
              result={devices}
              isLoading={devicesLoading}
              isRetrying={fleetQuery.isFetching}
              failed={devicesFailed}
              onRetry={() => void fleetQuery.refetch()}
              onNavigate={onNavigate}
            />
          ) : null}

          {routes.length > 0 ? <PageSearchResults routes={routes} onNavigate={onNavigate} /> : null}

          {routes.length === 0 && !showDevices ? (
            <div className="rounded-surface border border-border-subtle bg-surface-recessed p-4">
              <p className="font-semibold text-ink-strong">
                {trimmedQuery ? `Nothing matches “${trimmedQuery}”.` : 'No pages are available.'}
              </p>
              <p className="mt-1 text-body text-ink-muted">
                Try another word, or clear the search.
              </p>
            </div>
          ) : null}
        </div>
      </div>
    );
  },
);

ConsoleTaskSearch.displayName = 'ConsoleTaskSearch';

export default ConsoleTaskSearch;
